import $ from 'jquery';
import { AMPLITUDE_EVENTS, flushAnalytics, trackAnalytics } from '../Tracking/baglessAnalytics';

/**
 * Blocking consent dialog in view mode when enabled in space settings.
 * Acceptance is remembered per world + consent revision (localStorage).
 * Each accept/decline is stored in DB (space_consent_log) with userId and/or guestId.
 * Decline exits immediately: customExit URL (if present), Home URL, or close tab when customBack, else site origin.
 */
class SpaceConsentModal {
  constructor({ model }) {
    this.model = model;
    this.$modal = $('#space-consent-modal');
    if (!this.$modal.length) {
      return;
    }

    // Skip in embedded views (same as welcome / guest modals in setScene.js)
    if (model.isEmbed === '0' || model.isEmbed === '1') {
      return;
    }

    const cs = model.sceneData && model.sceneData.consentScreen;
    if (!cs || !cs.show) {
      return;
    }

    const worldId = model.config.worldId;
    const revision = cs.revision || 1;
    const storageKey = `bagless_space_consent_${worldId}_${revision}`;
    try {
      if (localStorage.getItem(storageKey) === '1') {
        return;
      }
    } catch (e) {
      /* ignore */
    }

    this._sessionString = new aptis.UUIDGenerator().generate();
    const title = (cs.title || '').trim() || 'Before you enter';
    const customLogo = (model.sceneData && model.sceneData.customLogo) || '';
    const logoSrc = customLogo && customLogo !== './textures/empty.png'
      ? customLogo
      : '/textures/bagless_logo.png';
    $('#space-consent-modal-title').text(title);
    $('#space-consent-modal-logo').attr('src', logoSrc);
    const html = cs.description || '';
    $('#space-consent-modal-body').html(html);
    this.$modal.addClass('is-active');

    $('#space-consent-accept')
      .off('click.spaceConsent')
      .on('click.spaceConsent', () => this._onAccept(storageKey, revision));
    $('#space-consent-decline')
      .off('click.spaceConsent')
      .on('click.spaceConsent', () => this._onDecline(revision));
  }

  _persistChoice(accepted, revision) {
    return this.model.saveSpaceConsentChoice({
      worldId: this.model.config.worldId,
      accepted,
      consentRevision: revision,
    });
  }

  _onAccept(storageKey, revision) {
    try {
      localStorage.setItem(storageKey, '1');
    } catch (e) {
      /* ignore */
    }
    this._persistChoice(true, revision).finally(() => {
      this.$modal.removeClass('is-active');
    });
  }

  _onDecline(revision) {
    this.model?.view?._executeSceneEventActions?.('consentDeclined');
    trackAnalytics(this.model, AMPLITUDE_EVENTS.DECLINED_CONSENT_DIALOGUE, {
      consent_type: 'space',
      consent_revision: revision,
    });
    Promise.all([
      this._persistChoice(false, revision),
      flushAnalytics(),
    ]).finally(() => {
      const customExit = (this.model.customExit || '').trim();
      if (customExit) {
        let url = customExit;
        if (!/^https?:\/\//i.test(url)) {
          url = `https://${url.replace(/^\/+/, '')}`;
        }
        window.location.href = url;
        return;
      }
      const home = (this.model.sceneData.homeURL || '').trim();
      if (home) {
        let url = home;
        if (!/^https?:\/\//i.test(url)) {
          url = `https://${url.replace(/^\/+/, '')}`;
        }
        window.location.href = url;
        return;
      }
      if (this.model.customBack) {
        window.close();
        return;
      }
      window.location.assign(window.origin);
    });
  }
}

export { SpaceConsentModal };
